import { GraphicPanel } from "./graphic-panel";
import { WebglObject } from "./webgl-object";
import { FixedDimension } from "../utils/fixed-dimension";

export class GraphicGrid extends WebglObject {
  /*
  settings: {
    rowsSpace: new ScalableDimension(0, 0),
    rows: [
      {
        height: new ScalableDimension(0, 0),
        columnsSpace: new ScalableDimension(0, 0),
        columns: [
          {
            width: new ScalableDimension(0, 0)
          }
        ]
      }
    ]
  } 
  */
  constructor(settings) {
    super(settings);

    let commonSpaceBetweenRows = 0;
    const rowsSpaceInPixels = this.settings.rowsSpace.calcValue(this.settings.clientSize.height);
    if (this.settings.rows.length > 1) {
      commonSpaceBetweenRows = rowsSpaceInPixels * (this.settings.rows.length - 1);
    }
    const panelsAreaHeight = this.settings.clientSize.height - commonSpaceBetweenRows;
    let cellY = 0;
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const rowInfo = this.settings.rows[rowIndex];
      const rowHeight = rowInfo.height.calcValue(panelsAreaHeight);
      const columnsSpaceInPixels = rowInfo.columnsSpace.calcValue(this.settings.clientSize.width);
      let cellX = 0;
      let commonSpaceBetweenColumns = 0;
      if (rowInfo.columns.length > 1) {
        commonSpaceBetweenColumns = columnsSpaceInPixels * (rowInfo.columns.length - 1);
      }
      const panelsAreaWidth = this.settings.clientSize.width - commonSpaceBetweenColumns;
      for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
        const columnInfo = rowInfo.columns[columnIndex];
        columnInfo.panel = new GraphicPanel({
          location: {
            x: new FixedDimension(cellX),
            y: new FixedDimension(this.settings.clientSize.height - cellY - rowHeight)
          },
          size: {
            width: columnInfo.width,
            height: rowInfo.height
          },
          parent: {
            ref: this,
            isCustom: true,
            location: {
              x: this.settings.absoluteLocation.x,
              y: this.settings.absoluteLocation.y
            },
            size: {
              width: panelsAreaWidth,
              height: panelsAreaHeight
            },
            padding: {
              left: this.settings.paddingInPixels.left,
              right: this.settings.paddingInPixels.right,
              top: this.settings.paddingInPixels.top,
              bottom: this.settings.paddingInPixels.bottom
            }
          }
        });
        cellX += columnInfo.width.calcValue(panelsAreaWidth) + columnsSpaceInPixels;
      }
      cellY += rowHeight + rowsSpaceInPixels;
    }
    this.settings.debug.boxFillColor = [0.0, 0.8, 0, 1];
    this.settings.debug.boxStrokeColor = [0, 0.2, 0, 1];
  }

  createBorders() {
    super.createBorders();
    let commonSpaceBetweenRows = 0;
    const rowsSpaceInPixels = this.settings.rowsSpace.calcValue(this.settings.clientSize.height);
    if (this.settings.rows.length > 1) {
      commonSpaceBetweenRows = rowsSpaceInPixels * (this.settings.rows.length - 1);
    }
    const panelsAreaHeight = this.settings.clientSize.height - commonSpaceBetweenRows;
    let cellY = 0;
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const rowInfo = this.settings.rows[rowIndex];
      const rowHeight = rowInfo.height.calcValue(panelsAreaHeight);
      const columnsSpaceInPixels = rowInfo.columnsSpace.calcValue(this.settings.clientSize.width);
      let cellX = 0;
      let commonSpaceBetweenColumns = 0;
      if (rowInfo.columns.length > 1) {
        commonSpaceBetweenColumns = columnsSpaceInPixels * (rowInfo.columns.length - 1);
      }
      const panelsAreaWidth = this.settings.clientSize.width - commonSpaceBetweenColumns;
      for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
        const columnInfo = rowInfo.columns[columnIndex];
        columnInfo.panel.settings.location.x.value = cellX;
        columnInfo.panel.settings.location.y.value = this.settings.clientSize.height - cellY - rowHeight;

        columnInfo.panel.setLocation(new FixedDimension(cellX), new FixedDimension(this.settings.clientSize.height - cellY - rowHeight));
        columnInfo.panel.setParentSize(panelsAreaWidth, panelsAreaHeight);
        columnInfo.panel.createBorders();
        cellX += columnInfo.width.calcValue(panelsAreaWidth) + columnsSpaceInPixels;
      }
      cellY += rowHeight + rowsSpaceInPixels;
    }
  }

  setRowHeight(rowIndex, height) {
    if (rowIndex < 0 || rowIndex >= this.settings.rows.length) {
      return;
    }
    let rowInfo = this.settings.rows[rowIndex];
    rowInfo.height = height;
    for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
      const panel = rowInfo.columns[columnIndex].panel;
      panel.updateSize(panel.settings.size.width, height);
    }
    // update borders of rows below
    let commonSpaceBetweenRows = 0;
    const rowsSpaceInPixels = this.settings.rowsSpace.calcValue(this.settings.clientSize.height);
    if (this.settings.rows.length > 1) {
      commonSpaceBetweenRows = rowsSpaceInPixels * (this.settings.rows.length - 1);
    }
    const panelsAreaHeight = this.settings.clientSize.height - commonSpaceBetweenRows;
    let cellY = this.settings.clientSize.height - rowInfo.columns[0].panel.settings.locationInPixels.y + rowsSpaceInPixels;
    for (let r = rowIndex + 1; r < this.settings.rows.length; r++) {
      rowInfo = this.settings.rows[r];
      let cellX = 0;
      const rowHeight = rowInfo.height.calcValue(panelsAreaHeight);
      const columnsSpaceInPixels = rowInfo.columnsSpace.calcValue(this.settings.clientSize.width);
      let commonSpaceBetweenColumns = 0;
      if (rowInfo.columns.length > 1) {
        commonSpaceBetweenColumns = columnsSpaceInPixels * (rowInfo.columns.length - 1);
      }
      const panelsAreaWidth = this.settings.clientSize.width - commonSpaceBetweenColumns;
      for (let columnIndex = 0; columnIndex < rowInfo.columns.length; columnIndex++) {
        const columnInfo = rowInfo.columns[columnIndex];
        columnInfo.panel.updateLocation(new FixedDimension(cellX), new FixedDimension(this.settings.clientSize.height - cellY - rowHeight));
        cellX += columnInfo.width.calcValue(panelsAreaWidth) + columnsSpaceInPixels;
      }
      cellY += rowHeight + rowsSpaceInPixels;
    }
  }

  setColumnWidth(rowIndex, columnIndex, width) {
    if (
      rowIndex < 0 ||
      rowIndex >= this.settings.rows.length ||
      columnIndex < 0 ||
      columnIndex >= this.settings.rows[rowIndex].columns.length
    ) {
      return;
    }
    const rowInfo = this.settings.rows[rowIndex];
    let cellInfo = rowInfo.columns[columnIndex];
    cellInfo.width = width;
    cellInfo.panel.updateSize(width, cellInfo.panel.settings.size.height);
    // update columns (in row with rowIndex) after column with columnIndex

    let commonSpaceBetweenColumns = 0;
    const columnsSpaceInPixels = rowInfo.columnsSpace.calcValue(this.settings.clientSize.width);
    if (rowInfo.columns.length > 1) {
      commonSpaceBetweenColumns = columnsSpaceInPixels * (rowInfo.columns.length - 1);
    }
    const panelsAreaWidth = this.settings.clientSize.width - commonSpaceBetweenColumns;

    let cellX = cellInfo.panel.settings.locationInPixels.x + cellInfo.width.calcValue(panelsAreaWidth) + columnsSpaceInPixels;
    const cellY = cellInfo.panel.settings.locationInPixels.y;
    for (let colIndex = columnIndex + 1; colIndex < rowInfo.columns.length; colIndex++) {
      cellInfo = rowInfo.columns[colIndex];
      cellInfo.panel.updateLocation(new FixedDimension(cellX), new FixedDimension(cellY));
      cellX += cellInfo.width.calcValue(panelsAreaWidth) + columnsSpaceInPixels;
    }
  }

  addItem(rowIndex, columnIndex, item) {
    const panel = this.settings.rows[rowIndex].columns[columnIndex].panel;
    panel.addItem(item);
  }

  setDebugMode(debugSettings) {
    super.setDebugMode(debugSettings);
    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const columns = this.settings.rows[rowIndex].columns;
      for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
        columns[columnIndex].panel.setDebugMode(debugSettings);
      }
    }
  }

  draw(gl, program) {
    super.draw(gl, program);

    for (let rowIndex = 0; rowIndex < this.settings.rows.length; rowIndex++) {
      const columns = this.settings.rows[rowIndex].columns;
      for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
        columns[columnIndex].panel.draw(gl, program);
      }
    }
  }
}
